import React, { useState, useMemo, useCallback, useEffect, useRef, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  ChevronRight,
  UserRound,
  Mail,
  Smartphone,
  SquarePen,
  Eye,
  ZoomIn,
  X,
  Rotate3D,
  MapPin, Globe, Mailbox, Building2,
} from 'lucide-react';
import { Header } from '../Common';
import Input from '@ajat/design-system/Input';
import { CheckboxMedium } from '@ajat/design-system/Checkbox';
import Button from '@ajat/design-system/Button';
import DatePicker from '@ajat/design-system/DatePicker';
import Popup from '@ajat/design-system/Popup';
import SidePanel from '@ajat/design-system/SidePanel';
import FileUploadSimple from '@ajat/design-system/FileUpload/Simple';
import {
  HeadingSmall,
  HeadingMedium,
  HeadingLarge,
  LabelSelector,
  LargeLabelSelector,
  ColorSelector,
  StickyFooter,
} from '@ajat/design-system/Configurator';
import { pushNotification } from '@ajat/design-system/Notification/useNotification';
import useSchools from '../../../../hooks/useSchools';
import { correctEmail } from '@ajat/design-system/utils/emailCorrection';
import blueFront from '../../../../assets/cls-dk/open-shop/blue-front.jpg';
import blueBack from '../../../../assets/cls-dk/open-shop/blue-back.jpg';
import beigeFront from '../../../../assets/cls-dk/open-shop/beige-front.jpg';
import beigeBack from '../../../../assets/cls-dk/open-shop/beige-back.jpg';
import exampleAreas from '../../../../assets/cls-dk/open-shop/example-areas.jpg';
import hoodieModelUrl from '@ajat/design-system/assets/models/hoodie-1.glb';
import GalleryBack from '../../Common/Product/GalleryBack';
import { getThreeDTemplate } from '@ajat/design-system/Product3DPreview/templates';
import { presetForUpload, BACK_PRINT_SIZE_PRESET } from './previewCamera';
import { CMS_API_URL } from '../../../../config/apiBase';
import { FALLBACK_COLORS, colorsFromVariation, inkForColor, PREVIEW_BRAND, PREVIEW_PRODUCT_SLUG, PREVIEW_VARIATION_ID } from './previewColors';
import { logoAssetsUrl, existingChestLogo } from './previewLogo';
import '../../Common/Product/ProductDetails.css';
import './CreateSchool.css';
import { pdfFileToPngUrl, isPdfFile } from './pdfToImage';
import { submitClassLogo } from './classLogoApi';

// Lazy-load 3D preview so three.js stays out of the main bundle
const Product3DPreview = lazy(() => import('@ajat/design-system/Product3DPreview'));

// Optimized Draco-compressed GLB. Webpack will emit it to /static/media/ with a hashed name.
const HOODIE_MODEL_URL = hoodieModelUrl;
// The hoodie template's model config brings its camera presets (schoolLogoFront, backPrint …)
// to the preview — without it only the front / back toggle views exist.
const HOODIE_MODEL_CONFIG = getThreeDTemplate('hoodie-1')?.modelConfig;

/* ══════════════════════════════════════════════════════════════════════════
 * CLS-DK — Create School / Open Shop request page
 * ══════════════════════════════════════════════════════════════════════════ */

const SCHOOL_STORAGE_KEY = 'clsdk_school_selection';
const SCHOOL_NOT_IN_LIST = '__not_in_list__';

// Preview shirt colours: the DK Royal Hoodie's enabled colours, fetched live (see
// previewColors.js); FALLBACK_COLORS (Mørk / Lys) until they arrive.

/* Ink preview palette (5 colors per Figma 8523:51557) */
const INK_COLORS = [
  { id: 'white', name: 'Hvid', swatch: '#FFFFFF' },
  { id: 'black', name: 'Sort', swatch: '#080C0A' },
  { id: 'red',   name: 'Rød',  swatch: '#C8102E' },
  { id: 'blue',  name: 'Blå',  swatch: '#1B365D' },
  { id: 'green', name: 'Grøn', swatch: '#0E7C3A' },
];

const IMAGES_BY_COLOR = {
  blue: [
    { src: blueFront, alt: 'Mørk forside' },
    { src: blueBack, alt: 'Mørk bagside' },
  ],
  beige: [
    { src: beigeFront, alt: 'Lys forside' },
    { src: beigeBack, alt: 'Lys bagside' },
  ],
};

const BRYSTLOGO_OPTIONS = [
  'Ja, skolens',
  'Nej',
  'Ja, vores eget design',
];

const TRYKFARVE_OPTIONS = [
  'Hvidt tryk (mørke trøjer)',
  'Sort tryk (lyse trøjer)',
];
// At most two print colours per class ("Der kan vælges 2 forskellige trykfarver"),
// counted across the two standard colours and the free-text "Anden farve".
const MAX_TRYKFARVER = 2;

const PLACERINGER_OPTIONS = ['1', '2', '3', '4', '5', '6'];

const PLACERING_INFO_ITEMS = [
  'Skolens logo venstre bryst',
  'Navnetryk højre bryst',
  'Tegning ryg',
  'Flagtryk standard øvre overarm',
  'Klassens bogstav',
  'Speciel tekst nederst på højre ærme',
];

/* Betalingsdato, «Hvilke produkter skal med?» and the payment-deadline text left the panel on the owner's word
   (2026-11-02); the backend keeps the old submissions' values and shows «—» for new ones. */
const EMPTY_REP = {
  name: '',
  email: '',
  phone: '',
  address: '',
  zipcode: '',
  city: '',
  destination: '',
  departureDate: '',
};

const formatRepDate = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat('da-DK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

const CreateSchoolPreviewLoader = () => (
  <div className="cs__preview-loader" role="status" aria-live="polite">
    <div className="cs__preview-loader-inner">
      <Rotate3D className="cs__preview-loader-icon" size={36} strokeWidth={1.5} />
      <p className="cs__preview-loader-title typo-h3">Drej</p>
      <p className="cs__preview-loader-copy typo-body">Brug musen eller din hånd til at dreje 3D-modellen</p>
      <div className="cs__preview-loader-track" aria-hidden="true">
        <span className="cs__preview-loader-progress" />
      </div>
      <p className="cs__preview-loader-label typo-h4">LOADING 3D PREVIEW</p>
    </div>
  </div>
);

/* Validators (Danish messages) */
const REP_VALIDATORS = {
  name:          (v) => v?.trim() ? '' : 'Indtast navn',
  email:         (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '') ? '' : 'Ugyldig e-mail',
  phone:         (v) => /^\d{7,15}$/.test((v || '').replace(/[\s\-+]/g, '')) ? '' : 'Ugyldigt telefonnummer',
  address:       (v) => v?.trim() ? '' : 'Indtast skolens adresse',
  // 4–6 digits like the checkout: Danish postcodes have 4, German schools (5) upload logos too (2026-10-26).
  zipcode:       (v) => /^\d{4,6}$/.test((v || '').replace(/\s/g, '')) ? '' : 'Postnummer skal være 4-6 cifre',
  city:          (v) => v?.trim() ? '' : 'Indtast by',
  departureDate: (v) => v ? '' : 'Vælg afrejsedato',
};

/* localStorage hydration */
function loadSavedSchool() {
  try {
    const raw = localStorage.getItem(SCHOOL_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

const CreateSchool = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledRef = useRef(false);
  const { schools, loading: schoolsLoading } = useSchools('clsdk');
  const [useMobilePreview, setUseMobilePreview] = useState(() => (
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 1023px)').matches
      : false
  ));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const query = window.matchMedia('(max-width: 1023px)');
    const handleChange = (event) => setUseMobilePreview(event.matches);
    setUseMobilePreview(query.matches);
    query.addEventListener?.('change', handleChange);
    return () => query.removeEventListener?.('change', handleChange);
  }, []);

  /* ── Step 1 state — school + class ── */
  const saved = useMemo(loadSavedSchool, []);
  const [schoolId, setSchoolId] = useState(saved?.school?.id ?? null);
  const [schoolName, setSchoolName] = useState(saved?.school?.name ?? '');
  const [schoolMode, setSchoolMode] = useState('searchable'); // 'searchable' | 'custom'
  const [schoolSearch, setSchoolSearch] = useState(saved?.school?.name ?? '');
  // Shop (a school's per-season setup). Assumed when the school has exactly one
  // open shop; a picker is shown when there are several; skipped when none.
  const [shopId, setShopId] = useState(saved?.shop?.id ?? null);
  const [shopName, setShopName] = useState(saved?.shop?.name ?? '');
  const [shopSearch, setShopSearch] = useState(saved?.shop?.name ?? '');
  const [className, setClassName] = useState(saved?.class ?? '');
  const [classMode, setClassMode] = useState('searchable');
  const [classSearch, setClassSearch] = useState(saved?.class ? `Klasse ${saved.class}` : '');
  // Whether the class came from the dropdown (existing) vs was typed (new).
  const [classFromList, setClassFromList] = useState(!!saved?.class);
  const [editingSchool, setEditingSchool] = useState(!saved);
  const [step1Error, setStep1Error] = useState('');
  const schoolCustomRef = useRef(null);

  // Scroll the custom school input into view after the mobile keyboard appears
  useEffect(() => {
    if (schoolMode !== 'custom') return undefined;
    const timer = setTimeout(() => {
      schoolCustomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 350);
    return () => clearTimeout(timer);
  }, [schoolMode]);

  // The selected school + its open shops (API returns them as `seasons`).
  const selectedSchool = useMemo(
    () => (schools ?? []).find((s) => String(s.id) === String(schoolId)) ?? null,
    [schools, schoolId],
  );
  const shopSeasons = useMemo(() => selectedSchool?.seasons ?? [], [selectedSchool]);
  const needsShopPick = shopSeasons.length >= 2;

  // Assume the only open shop; clear when the school has none; leave the pick to
  // the user when there are several. Runs when the selected school changes.
  useEffect(() => {
    if (shopSeasons.length === 1) {
      setShopId(shopSeasons[0].id);
      setShopName(shopSeasons[0].name);
      setShopSearch(shopSeasons[0].name);
    } else if (shopSeasons.length === 0) {
      setShopId(null);
      setShopName('');
      setShopSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, schools]);

  const shopChosen = !needsShopPick || !!shopId;
  const isStep1Done = (schoolId || (schoolMode === 'custom' && schoolName.trim()))
    && shopChosen && className.trim();
  const showConfigurator = isStep1Done && !editingSchool;

  // Prefill School / Shop / Class from ?school=&shop=&class= (admin "submit on
  // behalf" / "No Logo" links). Runs once after schools have loaded.
  useEffect(() => {
    if (prefilledRef.current || !(schools ?? []).length) return;
    const qSchool = searchParams.get('school');
    const qShop = searchParams.get('shop');
    const qClass = searchParams.get('class');
    if (!qSchool && !qClass) { prefilledRef.current = true; return; }

    const school = (schools ?? []).find(
      (s) => String(s.slug) === qSchool || String(s.id) === qSchool,
    );
    if (school) {
      setSchoolId(school.id);
      setSchoolName(school.name);
      setSchoolSearch(school.name);
      const shop = (school.seasons ?? []).find(
        (sh) => String(sh.slug) === qShop || String(sh.id) === qShop,
      );
      if (shop) {
        setShopId(shop.id);
        setShopName(shop.name);
        setShopSearch(shop.name);
      }
      if (qClass) {
        const fromList = (school.classes ?? []).some((c) => String(c) === qClass);
        setClassName(qClass);
        setClassSearch(fromList ? `Klasse ${qClass}` : qClass);
        setClassFromList(fromList);
        setClassMode('searchable');
      }
    }
    prefilledRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schools]);

  /* ── Color + 3D preview view ── */
  const [colors, setColors] = useState(FALLBACK_COLORS);
  const [selectedColor, setSelectedColor] = useState(FALLBACK_COLORS[0].id);
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const res = await fetch(
          `${CMS_API_URL}/api/products/${PREVIEW_BRAND}/${PREVIEW_PRODUCT_SLUG}?variation=${PREVIEW_VARIATION_ID}`,
          { signal: controller.signal, headers: { Accept: 'application/json' } },
        );
        if (!res.ok) return;
        const list = colorsFromVariation(await res.json());
        if (list.length) {
          setColors(list);
          // Keep the choice when it is one of the loaded colours, else start on the first.
          setSelectedColor((cur) => (list.some((c) => c.id === cur) ? cur : list[0].id));
          setSelectedInk((cur) => cur ?? inkForColor(list[0]));
        }
      } catch { /* keep the fallback colours */ }
    })();
    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const currentColor = colors.find((c) => c.id === selectedColor) || colors[0];
  const [preview3DView, setPreview3DView] = useState('front'); // 'front' | 'back'
  const [preview3DResetKey, setPreview3DResetKey] = useState(0);
  const [isPreviewZoomed, setIsPreviewZoomed] = useState(false);
  const scrollYBeforeZoom = useRef(0);
  // Fallback photo planes while the model loads: the dark or the light pair.
  const basePhotos = useMemo(() => {
    const pair = IMAGES_BY_COLOR[currentColor?.dark === false ? 'beige' : 'blue'];
    return { front: pair[0].src, back: pair[1].src };
  }, [currentColor]);

  const handleColorChange = useCallback((id) => {
    setSelectedColor(id);
    // Shirt colour proposes the matching ink (dark fabric → white, light → black);
    // the Ink preview selector below can still override it — and it drives
    // BOTH the artwork decals and the NAVNETRYK text.
    setSelectedInk(inkForColor(colors.find((c) => c.id === id) || id));
  }, [colors]);

  const handlePreviewViewChange = useCallback((nextView) => {
    setPreview3DView(nextView);
    setPreview3DResetKey((key) => key + 1);
  }, []);

  const openFullscreenPreview = useCallback(() => {
    if (!useMobilePreview) return;
    scrollYBeforeZoom.current = window.scrollY;
    setIsPreviewZoomed(true);
    window.scrollTo(0, 0);
  }, [useMobilePreview]);

  const closeFullscreenPreview = useCallback(() => {
    setIsPreviewZoomed(false);
    requestAnimationFrame(() => window.scrollTo(0, scrollYBeforeZoom.current));
  }, []);

  // Automatic camera: an upload / the back-print size turns the model to that print
  // (no fullscreen on mobile — only the explicit Preview link opens it).
  const focusPreview = useCallback((presetKey) => {
    setPreview3DView(presetKey);
    setPreview3DResetKey((key) => key + 1);
  }, []);

  const handlePreviewSide = useCallback((side) => {
    setPreview3DView(side);
    setPreview3DResetKey((key) => key + 1);
    if (useMobilePreview) {
      scrollYBeforeZoom.current = window.scrollY;
      setIsPreviewZoomed(true);
      window.scrollTo(0, 0);
    }
  }, [useMobilePreview]);

  useEffect(() => {
    if (!useMobilePreview && isPreviewZoomed) {
      setIsPreviewZoomed(false);
    }
  }, [useMobilePreview, isPreviewZoomed]);

  /* ── Upload state (file refs for submit payload + object URLs for 3D decals) ── */
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  // Back print format — drives the 3D preview scale and rides on the submission.
  const [backPrintSize, setBackPrintSize] = useState('a4');
  const [frontImageUrl, setFrontImageUrl] = useState(null);
  const [backImageUrl, setBackImageUrl] = useState(null);

  // Manage object URLs for the 3D decals (create on file set, revoke on
  // replace/unmount). Images map straight to an object URL; PDFs are rendered
  // to a PNG on the fly (three.js can't texture a PDF).
  const useDecalUrl = (file, setUrl) => {
    useEffect(() => {
      if (!file) { setUrl(null); return undefined; }

      if (file.type?.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setUrl(url);
        return () => URL.revokeObjectURL(url);
      }

      if (isPdfFile(file)) {
        let objectUrl = null;
        let cancelled = false;
        pdfFileToPngUrl(file)
          .then((url) => {
            if (cancelled) { URL.revokeObjectURL(url); return; }
            objectUrl = url;
            setUrl(url);
          })
          .catch((err) => {
            if (!cancelled) setUrl(null);
            // eslint-disable-next-line no-console
            console.error('Could not render PDF preview:', err);
          });
        return () => {
          cancelled = true;
          if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
      }

      setUrl(null);
      return undefined;
    }, [file, setUrl]);
  };

  useDecalUrl(frontFile, setFrontImageUrl);
  useDecalUrl(backFile, setBackImageUrl);

  // The school's EXISTING chest logo (same fallback chain as the product page) shows on
  // the preview until a front drawing is uploaded; refetched when school / shop / class change.
  const [existingLogoUrl, setExistingLogoUrl] = useState(null);
  const selectedShopSlug = shopSeasons.find((sh) => String(sh.id) === String(shopId))?.slug ?? null;
  useEffect(() => {
    const url = logoAssetsUrl(CMS_API_URL, PREVIEW_BRAND, selectedSchool?.slug, { shopSlug: selectedShopSlug, className: className || null });
    if (!url) { setExistingLogoUrl(null); return undefined; }
    const controller = new AbortController();
    (async () => {
      try {
        const res = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        setExistingLogoUrl(res.ok ? existingChestLogo(await res.json()) : null);
      } catch { /* aborted / offline: keep the plain chest */ }
    })();
    return () => controller.abort();
  }, [selectedSchool?.slug, selectedShopSlug, className]);

  /* ── Klasserepræsentanter ── */
  const [klasseRep, setKlasseRep] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [repDraft, setRepDraft] = useState(EMPTY_REP);
  const [repTouched, setRepTouched] = useState({});
  const [repError, setRepError] = useState('');

  const openRepSidebar = useCallback(() => {
    setRepDraft(klasseRep ?? EMPTY_REP);
    setRepTouched({});
    setSidebarOpen(true);
  }, [klasseRep]);

  const updateRepField = useCallback((field) => (eOrValue) => {
    const value = eOrValue?.target ? eOrValue.target.value : eOrValue;
    setRepDraft((p) => ({ ...p, [field]: value }));
  }, []);

  const repFieldError = useCallback((field) => {
    if (!repTouched[field]) return '';
    return REP_VALIDATORS[field]?.(repDraft[field]) ?? '';
  }, [repDraft, repTouched]);

  const handleRepBlur = useCallback((field) => () => {
    setRepTouched((p) => ({ ...p, [field]: true }));
    if (field === 'email') {
      setRepDraft((prev) => {
        if (!prev.email) return prev;
        const fixed = correctEmail(prev.email);
        return fixed !== prev.email ? { ...prev, email: fixed } : prev;
      });
    }
  }, []);

  const handleRepSave = useCallback(() => {
    const allTouched = Object.keys(REP_VALIDATORS).reduce((acc, k) => ({ ...acc, [k]: true }), {});
    setRepTouched(allTouched);
    const hasErrors = Object.entries(REP_VALIDATORS).some(
      ([field, fn]) => fn(repDraft[field])
    );
    if (hasErrors) return;
    setKlasseRep(repDraft);
    setSidebarOpen(false);
    setRepError('');
  }, [repDraft]);

  const handleRepSubmit = useCallback((event) => {
    event.preventDefault();
    handleRepSave();
  }, [handleRepSave]);

  /* ── Configurator selections ── */
  const [selectedInk, setSelectedInk] = useState('white');

  const [brystlogo, setBrystlogo] = useState('Ja, skolens');
  const [trykfarver, setTrykfarver] = useState(new Set());
  const [customTrykfarve, setCustomTrykfarve] = useState('');
  // "Anden farve (valgfri)" checkbox: the free-text field shows (and counts) only while ticked.
  const [useCustomTrykfarve, setUseCustomTrykfarve] = useState(false);
  const trykfarveCount = trykfarver.size + (useCustomTrykfarve ? 1 : 0);
  const [placeringer, setPlaceringer] = useState(new Set());
  const [customPlacering, setCustomPlacering] = useState('');
  const [placeringInfoOpen, setPlaceringInfoOpen] = useState(false);
  const [errors, setErrors] = useState({});

  // Clear individual errors as soon as the field becomes valid
  useEffect(() => {
    if (klasseRep) setErrors(prev => { if (!prev.klasseRep) return prev; const { klasseRep: _, ...rest } = prev; return rest; });
  }, [klasseRep]);
  useEffect(() => {
    if (brystlogo) setErrors(prev => { if (!prev.brystlogo) return prev; const { brystlogo: _, ...rest } = prev; return rest; });
  }, [brystlogo]);
  useEffect(() => {
    if (trykfarver.size > 0 || (useCustomTrykfarve && customTrykfarve.trim())) setErrors(prev => { if (!prev.trykfarver) return prev; const { trykfarver: _, ...rest } = prev; return rest; });
  }, [trykfarver, customTrykfarve, useCustomTrykfarve]);
  useEffect(() => {
    if (placeringer.size > 0 || customPlacering.trim()) setErrors(prev => { if (!prev.placeringer) return prev; const { placeringer: _, ...rest } = prev; return rest; });
  }, [placeringer, customPlacering]);

  const toggleSet = (setter) => (value) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value); else next.add(value);
      return next;
    });
  };

  /* ── Step 1 handlers ── */
  const schoolOptions = useMemo(() => {
    const fromApi = (schools ?? []).map((s) => ({ label: s.name, value: s.id }));
    return [...fromApi, { label: 'Min skole er ikke på listen', value: SCHOOL_NOT_IN_LIST }];
  }, [schools]);

  // Classes active in the selected school AND its school shop: the selected
  // shop's enabled classes (`seasons[].classes` — pivot-enabled + globally
  // active), the union of open shops until one is picked, and the school's
  // active list only when it has no open shop at all (pre-shop submissions).
  // Typing a new class stays possible either way.
  const classOptions = useMemo(() => {
    if (!schoolId) return [];
    const school = (schools ?? []).find((s) => s.id === schoolId);
    if (!school) return [];
    const seasons = school.seasons ?? [];
    let names;
    if (seasons.length === 0) {
      names = school.classes ?? [];
    } else {
      const shop = seasons.find((sh) => String(sh.id) === String(shopId));
      names = shop
        ? (shop.classes ?? [])
        : [...new Set(seasons.flatMap((sh) => sh.classes ?? []))];
    }
    return names.map((c) => ({ label: `Klasse ${c}`, value: c }));
  }, [schoolId, shopId, schools]);

  const shopOptions = useMemo(
    () => shopSeasons.map((sh) => ({ label: sh.name, value: sh.id })),
    [shopSeasons],
  );

  const handleShopSearchChange = useCallback((e) => {
    setShopSearch(e.target.value);
  }, []);

  const handleShopSelect = useCallback((opt) => {
    if (!opt) { setShopSearch(''); setShopId(null); setShopName(''); return; }
    setShopId(opt.value);
    setShopName(opt.label);
    setShopSearch(opt.label);
  }, []);

  const handleSchoolSearchChange = useCallback((e) => {
    setSchoolSearch(e.target.value);
  }, []);

  const handleSchoolSelect = useCallback((opt) => {
    if (!opt) {
      setSchoolSearch('');
      setSchoolId(null);
      setSchoolName('');
      return;
    }
    if (opt.value === SCHOOL_NOT_IN_LIST) {
      setSchoolMode('custom');
      setClassMode('custom');
      setSchoolSearch('');
      setSchoolId(null);
      setSchoolName('');
      setClassName('');
      setClassSearch('');
      return;
    }
    setSchoolId(opt.value);
    setSchoolName(opt.label);
    setSchoolSearch(opt.label);
    // Reset dependent selections when the school changes.
    setShopId(null);
    setShopName('');
    setShopSearch('');
    setClassName('');
    setClassSearch('');
    setClassMode('searchable');
    setClassFromList(false);
  }, []);

  const handleSchoolCustomChange = useCallback((e) => {
    const val = e.target.value;
    if (val === '') {
      setSchoolMode('searchable');
      setClassMode('searchable');
      setSchoolName('');
      setClassName('');
      return;
    }
    setSchoolName(val);
  }, []);

  const handleClassSearchChange = useCallback((e) => {
    setClassSearch(e.target.value);
    setClassName(e.target.value);
    setClassFromList(false);
  }, []);

  const handleClassSelect = useCallback((opt) => {
    if (!opt) {
      setClassSearch('');
      setClassName('');
      setClassFromList(false);
      return;
    }
    setClassSearch(opt.label);
    setClassName(opt.value);
    setClassFromList(true);
  }, []);

  const handleClassCustomChange = useCallback((e) => {
    setClassName(e.target.value);
    setClassFromList(false);
  }, []);

  const handleStep1Continue = useCallback(() => {
    if (!schoolId && !(schoolMode === 'custom' && schoolName.trim())) {
      setStep1Error('Vælg eller indtast din skole');
      return;
    }
    if (needsShopPick && !shopId) {
      setStep1Error('Vælg en butik');
      return;
    }
    if (!className.trim()) {
      setStep1Error('Vælg eller indtast din klasse');
      return;
    }
    setStep1Error('');
    setEditingSchool(false);
  }, [schoolId, schoolMode, schoolName, needsShopPick, shopId, className]);

  const handleStep1Edit = useCallback(() => {
    setEditingSchool(true);
  }, []);

  /* ── Submit ── */
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    const newErrors = {};
    if (!brystlogo) newErrors.brystlogo = 'Vælg en option';
    if (useCustomTrykfarve && !customTrykfarve.trim()) newErrors.trykfarver = 'Skriv den ønskede farve';
    else if (trykfarver.size === 0 && !useCustomTrykfarve) newErrors.trykfarver = 'Vælg mindst én farve';
    if (placeringer.size === 0 && !customPlacering.trim()) newErrors.placeringer = 'Vælg mindst én placering';
    if (!klasseRep) newErrors.klasseRep = 'Tilføj en klasserepræsentant';
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      requestAnimationFrame(() => {
        const els = Object.keys(newErrors)
          .map(key => document.querySelector(`[data-cs-section="${key}"]`))
          .filter(Boolean)
          .sort((a, b) =>
            a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
          );
        if (els[0]) els[0].scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }

    // A school from the list is required so we can attach the submission.
    if (!schoolId) {
      setEditingSchool(true);
      setStep1Error('Vælg din skole fra listen');
      requestAnimationFrame(() => {
        document.querySelector('[data-cs-section="step1"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }

    setSubmitting(true);
    try {
      await submitClassLogo('clsdk', {
        schoolId,
        shopId,
        className,
        classFromList,
        color: currentColor?.name || selectedColor,
        ink: selectedInk,
        klasseRep,
        brystlogo,
        trykfarver: [...trykfarver, ...(useCustomTrykfarve && customTrykfarve.trim() ? [customTrykfarve.trim()] : [])],
        placeringer: [...placeringer, ...(customPlacering.trim() ? [customPlacering.trim()] : [])],
        frontFile,
        backFile,
        extra: { backPrintSize: backPrintSize.toUpperCase() },
      });
      pushNotification({
        type: 'success',
        title: 'Tak for din henvendelse!',
        message: 'Vi vender tilbage hurtigst muligt.',
      });
      navigate('/clothing/cls-dk');
    } catch (err) {
      pushNotification({
        type: 'error',
        title: 'Noget gik galt',
        message: err?.message || 'Prøv venligst igen.',
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    brystlogo, trykfarver, customTrykfarve, useCustomTrykfarve, placeringer, customPlacering, klasseRep,
    schoolId, shopId, className, classFromList, selectedColor, selectedInk, frontFile, backFile, backPrintSize, navigate,
  ]);

  /* ══════════════════════════════════════════════════════════════════════ */
  /* Render helpers                                                         */
  /* ══════════════════════════════════════════════════════════════════════ */

  const renderStep1 = () => {
    if (!editingSchool && isStep1Done) {
      return (
        <div className="cs__compressed" data-cs-section="step1">
          <div className="cs__compressed-text">
            <span className="cs__compressed-name typo-h4">{schoolName}</span>
            <p className="cs__compressed-line typo-body">Klasse {className}</p>
          </div>
          <Button
            size="tertiary"
            variant="black"
            iconOnly
            iconLeft={<SquarePen size={20} />}
            onClick={handleStep1Edit}
            aria-label="Rediger skole"
            className="cs__compressed-edit"
          />
        </div>
      );
    }

    return (
      <div className="cs__step-card" data-cs-section="step1">
        {schoolMode === 'searchable' ? (
          <Input
            type="searchable"
            label="Skole"
            value={schoolSearch}
            options={schoolOptions}
            onChange={handleSchoolSearchChange}
            onSelect={handleSchoolSelect}
            showIcon={false}
            showOptionIcons={false}
            mobileFocus
            disabled={schoolsLoading}
          />
        ) : (
          <div ref={schoolCustomRef}>
            <Input
              key="school-custom"
              label="Type your school name"
              value={schoolName}
              showIcon={false}
              showClear
              autoFocus
              onChange={handleSchoolCustomChange}
            />
          </div>
        )}

        {/* Shop selector: dropdown when the school has several open shops; a
            read-only line when there's exactly one; nothing when there are none. */}
        {schoolId && needsShopPick && (
          <Input
            type="searchable"
            label="Butik"
            value={shopSearch}
            options={shopOptions}
            onChange={handleShopSearchChange}
            onSelect={handleShopSelect}
            showIcon={false}
            showOptionIcons={false}
            mobileFocus
          />
        )}
        {schoolId && !needsShopPick && shopName && (
          <div className="cs__readonly-shop typo-body">
            Butik: <strong>{shopName}</strong>
          </div>
        )}

        {(schoolId || (schoolMode === 'custom' && schoolName.trim())) && (
          classMode === 'searchable' ? (
            <Input
              type="searchable"
              label="Klasse"
              value={classSearch}
              options={classOptions}
              onChange={handleClassSearchChange}
              onSelect={handleClassSelect}
              showIcon={false}
              showOptionIcons={false}
              mobileFocus
            />
          ) : (
            <Input
              label="Klasse"
              value={className}
              showIcon={false}
              onChange={handleClassCustomChange}
            />
          )
        )}

        {step1Error && (
          <p className="cs__field-error typo-body-sm" role="alert">{step1Error}</p>
        )}

        <Button
          size="primary"
          variant="alternative"
          iconRight={<ChevronRight size={20} />}
          onClick={handleStep1Continue}
          disabled={!isStep1Done}
        >
          Frem
        </Button>
      </div>
    );
  };

  const renderKlasseRep = () => {
    if (klasseRep) {
      return (
        <div className="cs__compressed">
          <div className="cs__compressed-text">
            <span className="cs__compressed-name typo-h4">{klasseRep.name}</span>
            <div className="cs__rep-summary">
              <p className="cs__compressed-line typo-body">
                {klasseRep.phone}, {klasseRep.email}
              </p>
              <p className="cs__compressed-line typo-body">
                Afrejsedato: {formatRepDate(klasseRep.departureDate)}
                {klasseRep.destination ? ` · Destination: ${klasseRep.destination}` : ''}
              </p>
            </div>
          </div>
          <Button
            size="tertiary"
            variant="black"
            iconOnly
            iconLeft={<SquarePen size={20} />}
            onClick={openRepSidebar}
            aria-label="Rediger klasserepræsentant"
            className="cs__compressed-edit"
          />
        </div>
      );
    }
    return (
      <button
        type="button"
        className="cs__klasse-trigger"
        onClick={openRepSidebar}
        aria-label="Tilføj klasseshop information"
      >
        <span className="cs__klasse-trigger-icon cs__klasse-trigger-icon--left">
          <UserRound size={20} strokeWidth={2} />
        </span>
        <span className="cs__klasse-trigger-label typo-input">
          Klasseshop information
        </span>
        <span className="cs__klasse-trigger-icon cs__klasse-trigger-icon--right">
          <SquarePen size={20} strokeWidth={1.5} />
        </span>
      </button>
    );
  };

  return (
    <div className={`pp cs ${useMobilePreview && isPreviewZoomed ? 'pp--zoomed' : ''}${showConfigurator ? ' cs--has-footer' : ''}`}>
      {/* HEADER */}
      <div className="pp__header">
        <Header hideOnScroll={false} showTopBar={false} schoolSelectorLockScroll={false} />
      </div>
      <div className="pp__header-mobile">
        <Header hideOnScroll={false} showTopBar={false} schoolSelectorLockScroll={false} />
      </div>

      {/* MAIN */}
      <main className="pp__main">
        <div className="pp__gallery cs__gallery-3d">
          <GalleryBack />
          <div className="cs__preview-slot">
            <Suspense fallback={<CreateSchoolPreviewLoader />}>
              <Product3DPreview
                modelUrl={HOODIE_MODEL_URL}
                modelConfig={HOODIE_MODEL_CONFIG}
                basePhotos={basePhotos}
                bodyColor={currentColor?.tint ?? currentColor?.swatch}
                frontImage={frontImageUrl ?? existingLogoUrl}
                // The school's existing logo may be white-on-black (imported / brand default),
                // like on the product page: the preview checks its pixels and keys out a dark
                // backdrop. An uploaded drawing is dark-on-white and is flattened as before.
                frontImageInverted={!frontImageUrl && !!existingLogoUrl}
                backImage={backImageUrl}
                text="NAVNETRYK"
                textColor={INK_COLORS.find((c) => c.id === selectedInk)?.swatch}
                artworkColor={INK_COLORS.find((c) => c.id === selectedInk)?.swatch}
                backArtworkScale={backPrintSize === 'a3' ? Math.SQRT2 : 1}
                view={preview3DView}
                onViewChange={handlePreviewViewChange}
                viewResetKey={preview3DResetKey}
                showToggle={useMobilePreview && isPreviewZoomed}
              />
            </Suspense>
          </div>
          {!isPreviewZoomed && (
            <div className="pp__zoom-btn-container cs__zoom-btn-container">
              <Button
                size="tertiary"
                variant="white"
                iconOnly
                iconLeft={<ZoomIn size={20} />}
                onClick={openFullscreenPreview}
                aria-label="Open fullscreen preview"
              />
            </div>
          )}
          {useMobilePreview && isPreviewZoomed && (
            <div className="pp__zoom-close-container cs__zoom-close-container">
              <Button
                size="tertiary"
                variant="gray"
                iconOnly
                iconLeft={<X size={20} />}
                onClick={closeFullscreenPreview}
                aria-label="Close fullscreen preview"
              />
            </div>
          )}
        </div>

        <div className="pp__configurator">
          <HeadingLarge
            title="Upload din tegning"
            description={"Upload jeres tegning/logo her og se et preview af designet. Det er ikke nødvendigt at uploade filen, før I bestiller trøjerne. I modtager derefter en email med leveringsdato og godkendelse af tegningen."}
          />

          {/* Step 1 — School + Class */}
          <div className="pp__section">
            <HeadingMedium title="Din skole" />
            {renderStep1()}
          </div>

          {showConfigurator && (
            <>
              {/* Design (HeadingMedium group) */}
              <div className="pp__section">
                <HeadingMedium title="Design" />
                <div className="cs__sub-option">
                  <HeadingSmall
                    title="Preview shirt color"
                    selection={currentColor?.name?.toUpperCase()}
                    showInfoButton={false}
                    showExtraInfo={false}
                  />
                  <div className="pp__color-row">
                    {colors.map((c) => (
                      <ColorSelector
                        key={c.id}
                        color={c.swatch}
                        name={c.name}
                        selected={selectedColor === c.id}
                        onClick={() => handleColorChange(c.id)}
                      />
                    ))}
                  </div>
                </div>
                <div className="cs__sub-option">
                  <HeadingSmall
                    title="Ink preview"
                    selection={INK_COLORS.find((c) => c.id === selectedInk)?.name?.toUpperCase()}
                    showInfoButton={false}
                    showExtraInfo={false}
                  />
                  <div className="pp__color-row">
                    {INK_COLORS.map((c) => (
                      <ColorSelector
                        key={c.id}
                        color={c.swatch}
                        name={c.name}
                        selected={selectedInk === c.id}
                        onClick={() => setSelectedInk(c.id)}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Upload tegning (HeadingMedium + Info + Forsiden + Bagsiden) */}
              <div className="pp__section">
                <HeadingMedium title="Upload tegning" />
                <div className="cs__info">
                  <p className="typo-body">
                    Det uploadede billede skal være pdf, jpeg eller png, ikke større end 8MB, 3000x3000px maks.
                  </p>
                </div>
                {[
                  { side: 'front', title: 'Skolelogo', setFile: setFrontFile },
                  { side: 'back',  title: 'Bagsiden tegning', setFile: setBackFile  },
                ].map(({ side, title, setFile }) => (
                  <div key={side} className="cs__upload-group">
                    <div className="cs__upload-top">
                      <HeadingSmall
                        title={title}
                        showSelection={false}
                        showInfoButton={false}
                        showExtraInfo={false}
                      />
                      <button
                        type="button"
                        className="cs__upload-link typo-link"
                        onClick={() => handlePreviewSide(side)}
                      >
                        <Eye size={16} strokeWidth={1.5} />
                        <span>Preview</span>
                      </button>
                    </div>
                    <FileUploadSimple
                      label="Choose file"
                      helperText="8MB, 3000x3000px, PDF, JPG, PNG"
                      accept="image/*,application/pdf"
                      onUpload={(file) => {
                        setFile(file);
                        focusPreview(presetForUpload(side, file));
                        return Promise.resolve();
                      }}
                      onClear={() => { setFile(null); focusPreview(presetForUpload(side, null)); }}
                    />
                    {side === 'back' && (
                      <div className="cs__sub-option">
                        <HeadingSmall
                          title="Størrelse på rygtryk"
                          selection={backPrintSize.toUpperCase()}
                          showInfoButton={false}
                          showExtraInfo={false}
                        />
                        <div className="cs__option-row">
                          {['a4', 'a3'].map((size) => (
                            <LargeLabelSelector
                              key={size}
                              selected={backPrintSize === size}
                              onClick={() => { setBackPrintSize(size); focusPreview(BACK_PRINT_SIZE_PRESET); }}
                            >
                              {size.toUpperCase()}
                            </LargeLabelSelector>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Klasserepræsentanter (HeadingMedium) */}
              <div className="pp__section" data-cs-section="klasseRep">
                <HeadingMedium title="Klasserepræsentanter" />
                {renderKlasseRep()}
                {errors.klasseRep && (
                  <p className="cs__field-error typo-body-sm" role="alert">
                    {errors.klasseRep}
                  </p>
                )}
              </div>

              {/* Tryk og placeringer (HeadingMedium group) */}
              <div className="pp__section">
                <HeadingMedium title="Tryk og placeringer" />

                {/* Brystlogo (single-select) */}
                <div className="cs__sub-option" data-cs-section="brystlogo">
                  <HeadingSmall
                    title="Ønsker I brystlogo?"
                    selection={brystlogo?.toUpperCase()}
                    showInfoButton={false}
                    showExtraInfo={false}
                  />
                  <div className="cs__option-row">
                    {BRYSTLOGO_OPTIONS.map((opt) => (
                      <LargeLabelSelector
                        key={opt}
                        selected={brystlogo === opt}
                        onClick={() => setBrystlogo(opt)}
                      >
                        {opt}
                      </LargeLabelSelector>
                    ))}
                  </div>
                  {errors.brystlogo && (
                    <p className="cs__field-error typo-body-sm" role="alert">
                      {errors.brystlogo}
                    </p>
                  )}
                </div>

                {/* Trykfarve (multi-select + custom) */}
                <div className="cs__sub-option" data-cs-section="trykfarver">
                  <HeadingSmall
                    title="Hvilken trykfarve ønskes?"
                    showSelection={false}
                    showInfoButton={false}
                    showExtraInfo={false}
                  />
                  <p className="cs__sub-option-subtitle typo-body-sm">Der kan vælges 2 forskellige trykfarver</p>
                  <div className="cs__checkbox-list">
                    {TRYKFARVE_OPTIONS.map((opt) => (
                      <CheckboxMedium
                        key={opt}
                        title={opt}
                        checked={trykfarver.has(opt)}
                        disabled={!trykfarver.has(opt) && trykfarveCount >= MAX_TRYKFARVER}
                        onChange={() => toggleSet(setTrykfarver)(opt)}
                      />
                    ))}
                    <CheckboxMedium
                      title="Anden farve (valgfri)"
                      checked={useCustomTrykfarve}
                      disabled={!useCustomTrykfarve && trykfarveCount >= MAX_TRYKFARVER}
                      onChange={(e) => setUseCustomTrykfarve(e.target.checked)}
                    />
                  </div>
                  {useCustomTrykfarve && (
                    <div className="cs__custom-color">
                      <Input
                        label="Anden farve"
                        value={customTrykfarve}
                        showIcon={false}
                        showClear
                        onChange={(e) => setCustomTrykfarve(e.target.value)}
                      />
                    </div>
                  )}
                  {errors.trykfarver && (
                    <p className="cs__field-error typo-body-sm" role="alert">
                      {errors.trykfarver}
                    </p>
                  )}
                </div>

                {/* Placeringer (multi-select numbered areas) */}
                <div className="cs__sub-option" data-cs-section="placeringer">
                  <HeadingSmall
                    title="Placeringer af tryk"
                    selection={[...placeringer].join(', ') || undefined}
                    infoTooltip="Se placeringer"
                    onInfoClick={() => setPlaceringInfoOpen(true)}
                    showExtraInfo={false}
                  />
                  <div className="cs__placering-preview">
                    <img src={exampleAreas} alt="Eksempel på placeringer" />
                  </div>
                  <div className="cs__option-row">
                    {PLACERINGER_OPTIONS.map((opt) => (
                      <LabelSelector
                        key={opt}
                        selected={placeringer.has(opt)}
                        onClick={() => toggleSet(setPlaceringer)(opt)}
                      >
                        {opt}
                      </LabelSelector>
                    ))}
                  </div>
                  <div className="cs__custom-color">
                    <Input
                      label="Anden placering (valgfri)"
                      value={customPlacering}
                      showIcon={false}
                      showClear
                      helperText="Ønskes anden placering end ovenstående, kan I skrive det herunder"
                      onChange={(e) => setCustomPlacering(e.target.value)}
                    />
                  </div>
                  {errors.placeringer && (
                    <p className="cs__field-error typo-body-sm" role="alert">
                      {errors.placeringer}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* STICKY FOOTER */}
      {showConfigurator && (
        <StickyFooter
          buttonLabel="Gem"
          buttonVariant="alternative"
          buttonLoading={submitting}
          showDelivery={false}
          price=""
          priceLabel=""
          onButtonClick={handleSubmit}
          className="pp__footer cs__footer"
        />
      )}

      {/* Klasserepræsentant SidePanel */}
      <SidePanel
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        title="Klasserepræsentant"
      >
        <form className="cs__rep-form" onSubmit={handleRepSubmit}>
          <div className="checkout__form-card cs__rep-form-card">
            <div className="checkout__form-row">
              <div className="checkout__form-cell">
                <Input
                  label="Navn"
                  value={repDraft.name}
                  icon={UserRound}
                  autoComplete="name"
                  showSuccess={!!repDraft.name && !REP_VALIDATORS.name(repDraft.name)}
                  error={repFieldError('name')}
                  onChange={updateRepField('name')}
                  onBlur={handleRepBlur('name')}
                />
              </div>
            </div>

            <div className="checkout__form-row">
              <div className="checkout__form-cell">
                <Input
                  type="email"
                  label="E-mail"
                  value={repDraft.email}
                  icon={Mail}
                  autoComplete="email"
                  showSuccess={!!repDraft.email && !REP_VALIDATORS.email(repDraft.email)}
                  error={repFieldError('email')}
                  onChange={updateRepField('email')}
                  onBlur={handleRepBlur('email')}
                />
              </div>
              <div className="checkout__form-cell">
                <Input
                  type="tel"
                  label="Telefon"
                  value={repDraft.phone}
                  icon={Smartphone}
                  autoComplete="tel"
                  showSuccess={!!repDraft.phone && !REP_VALIDATORS.phone(repDraft.phone)}
                  error={repFieldError('phone')}
                  onChange={updateRepField('phone')}
                  onBlur={handleRepBlur('phone')}
                />
              </div>
            </div>

            <div className="checkout__form-row">
              <div className="checkout__form-cell">
                <Input
                  label="Skolens adresse"
                  value={repDraft.address}
                  icon={MapPin}
                  autoComplete="street-address"
                  showSuccess={!!repDraft.address && !REP_VALIDATORS.address(repDraft.address)}
                  error={repFieldError('address')}
                  onChange={updateRepField('address')}
                  onBlur={handleRepBlur('address')}
                />
              </div>
            </div>

            <div className="checkout__form-row">
              <div className="checkout__form-cell">
                <Input
                  type="tel"
                  label="Postnummer"
                  value={repDraft.zipcode}
                  icon={Mailbox}
                  autoComplete="postal-code"
                  showSuccess={!!repDraft.zipcode && !REP_VALIDATORS.zipcode(repDraft.zipcode)}
                  error={repFieldError('zipcode')}
                  onChange={updateRepField('zipcode')}
                  onBlur={handleRepBlur('zipcode')}
                />
              </div>
              <div className="checkout__form-cell">
                <Input
                  label="By"
                  value={repDraft.city}
                  icon={Building2}
                  autoComplete="address-level2"
                  showSuccess={!!repDraft.city && !REP_VALIDATORS.city(repDraft.city)}
                  error={repFieldError('city')}
                  onChange={updateRepField('city')}
                  onBlur={handleRepBlur('city')}
                />
              </div>
            </div>

            <div className="checkout__form-row">
              <div className="checkout__form-cell">
                <Input
                  label="Destinationsland"
                  value={repDraft.destination}
                  icon={Globe}
                  showSuccess={!!repDraft.destination}
                  onChange={updateRepField('destination')}
                />
              </div>
            </div>

            <div className="checkout__form-row">
              <div className="checkout__form-cell">
                <DatePicker
                  label="Afrejsedato"
                  value={repDraft.departureDate}
                  error={repFieldError('departureDate')}
                  onBlur={handleRepBlur('departureDate')}
                  onChange={(val) => {
                    updateRepField('departureDate')(val);
                    setRepTouched((p) => ({ ...p, departureDate: true }));
                  }}
                />
              </div>
            </div>
          </div>

          {repError && (
            <p className="cs__field-error typo-body-sm" role="alert">{repError}</p>
          )}

          <div className="cs__sidebar-cta">
            <Button
              type="submit"
              size="primary"
              variant="alternative"
              iconRight={<ArrowRight size={20} />}
            >
              Gem
            </Button>
          </div>
        </form>
      </SidePanel>

      <Popup
        isOpen={placeringInfoOpen}
        onClose={() => setPlaceringInfoOpen(false)}
        title="Placeringer af tryk"
        showInfo={false}
        showMessage={false}
        showCancel={false}
        confirmLabel="Luk"
        confirmVariant="black"
        lockScroll={false}
        className="cs__placement-popup"
      >
        <ol className="cs__info-list">
          {PLACERING_INFO_ITEMS.map((item) => (
            <li key={item} className="typo-body">
              {item}
            </li>
          ))}
        </ol>
      </Popup>

    </div>
  );
};

export default CreateSchool;
